// packages/api/src/hooks/admin/pricing-admin/useAcceptanceRules.ts
// Phase 02.3-pricing-admin W3.2 — Admin acceptance rules + evaluator.
// NOTE: backend mounts `AcceptanceRulesController` at `/admin/acceptance-rules`
// (NOT under `/admin/pricing/`). See api-alamo/src/modules/pricing/admin/
// acceptance-rules.controller.ts:86. Phase 02.3.3 pricing-admin-08 cross-stack fix.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  AcceptanceEvaluation,
  AcceptanceRuleChannel,
  AcceptanceRuleFuelType,
  AcceptanceRuleGarageLocation,
  AcceptanceRuleUsageType,
  AdminAcceptanceRule,
  PaginatedResponse,
} from '@alamo/types';

import { apiClient } from '../../../client';
import { pricingAdminKeys, pricingChangeSetKeys } from '../../../query-keys';

const BASE = '/admin/acceptance-rules';

/**
 * Filtros da listagem admin de acceptance-rules.
 *
 * ESCR-198.11 (Plan 07, 2026-05-14) — Pagination Standard aplicado conforme
 * `docs/filters/2026-04-28/PAGINATION-STANDARD.md`. Backend já paginava server-side
 * desde ESCR-186.10 (contract `docs/contracts/ESCR-186.10.md`); o hook agora
 * propaga `page`/`limit` + filtros tipados.
 *
 * Phase 08.4 Plan 05 — paridade Pilar 3 (commit 448e9e4 backend): filtro
 * `csStatus` propaga EXISTS subquery em `change_set_items.target_entity_type=
 * 'acceptance_rule'`. Permite chips dedicados (Todas/Em rascunho/Aguardando
 * aprovação) no AcceptanceSection.
 */
export interface ListAcceptanceRulesFilters {
  // ESCR-198.8 — `scopeType` filter dropado.
  action?: 'reject' | 'manual_approval' | 'warn';
  status?: string;
  search?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
  csStatus?: 'draft' | 'approval_pending';
  /**
   * Filtro combinado dos chips "Em rascunho"/"Aguardando aprovação": casa a
   * regra quando o próprio `status` é X (entry-direct) OU quando há CS item
   * nesse status (edição de regra publicada). Backend: `pendingState`.
   */
  pendingState?: 'draft' | 'approval_pending';
}

/**
 * Lista paginada de regras admin. Retorna `PaginatedResponse<AdminAcceptanceRule>`
 * (canônico conforme `tables.md` + `PAGINATION-STANDARD.md` § 3.1).
 *
 * Backend retorna `{ count, rows }` + alias `{ items, meta }` (retrocompat
 * documentado no contrato ESCR-186.10). O hook normaliza para o shape
 * canônico `{ items, total, page, limit, totalPages }`.
 */
export function useAcceptanceRulesAdmin(
  filters?: ListAcceptanceRulesFilters,
  options?: { enabled?: boolean },
): ReturnType<typeof useQuery<PaginatedResponse<AdminAcceptanceRule>>> {
  const page = filters?.page ?? 1;
  const limit = filters?.limit ?? 20;
  return useQuery<PaginatedResponse<AdminAcceptanceRule>>({
    enabled: options?.enabled ?? true,
    queryKey: pricingAdminKeys.acceptanceRules({
      ...(filters as Record<string, unknown> | undefined),
      page,
      limit,
    }),
    queryFn: async () => {
      // ResponseInterceptor envelopa o payload do service em
      // `{ success, data, timestamp }`. O service pode retornar 3 shapes:
      //   - `{ count, rows }`   — Pagination Standard 2026-04-28 (canônico)
      //   - `{ items, meta }`   — alias retrocompat (ESCR-186.10)
      //   - `AdminAcceptanceRule[]` — listagem não-paginada (legado)
      const res = await apiClient.get<{
        data:
          | { count: number; rows: AdminAcceptanceRule[] }
          | {
              items: AdminAcceptanceRule[];
              meta: { page?: number; pageSize?: number; limit?: number; total: number };
            }
          | AdminAcceptanceRule[];
      }>(BASE, { params: { ...filters, page, limit } });

      const payload = res.data?.data;
      let items: AdminAcceptanceRule[];
      let total: number;

      if (Array.isArray(payload)) {
        // Legado — backend velho sem paginação. Trata o array todo como uma página.
        items = payload;
        total = payload.length;
      } else if ('count' in payload && 'rows' in payload) {
        // Canônico (Pagination Standard 2026-04-28).
        items = payload.rows;
        total = payload.count;
      } else if ('items' in payload && 'meta' in payload) {
        // Alias retrocompat ESCR-186.10.
        items = payload.items;
        total = payload.meta.total;
      } else {
        items = [];
        total = 0;
      }

      const totalPages = Math.max(1, Math.ceil(total / limit));
      return { items, total, page, limit, totalPages };
    },
  });
}

export function useAcceptanceRuleAdmin(id: string) {
  return useQuery({
    queryKey: pricingAdminKeys.acceptanceRule(id),
    queryFn: async () => {
      const res = await apiClient.get<{ data: AdminAcceptanceRule }>(`${BASE}/${id}`);
      return res.data.data;
    },
    enabled: Boolean(id),
  });
}

/**
 * ESCR-198.1..198.8 (2026-05-12) — payload V2 com critérios tipados.
 * Backend já aceita esses campos desde 186.1. `scopeType`, `condition` JSON,
 * `priority`, `rejectionMessagePtBr/En` foram dropados na migration 0098.
 */
export interface UpsertAcceptanceRuleInput {
  id?: string;
  name: string;
  description?: string | null;
  // ESCR-186.10 (migration 0115, 2026-05-14): aliases require_approval e
  // adjust dropados do CHECK constraint ar_action_check.
  action: 'reject' | 'manual_approval' | 'warn';
  // Action-conditional payload
  rejectionCode?: string | null;
  messagePtBr?: string | null;
  messageEnUs?: string | null;
  approvalQueueReason?: string | null;
  warnText?: string | null;
  // Vehicle
  categoryIds?: string[] | null;
  subgroupIds?: string[] | null;
  modelIds?: string[] | null;
  yearMin?: number | null;
  yearMax?: number | null;
  fipeMin?: string | null;
  fipeMax?: string | null;
  fuelTypes?: AcceptanceRuleFuelType[] | null;
  isArmored?: boolean | null;
  hasGasKit?: boolean | null;
  isBlackPlate?: boolean | null;
  kmMin?: number | null;
  kmMax?: number | null;
  noFipe?: boolean | null;
  // Usage / Garage
  usageTypes?: AcceptanceRuleUsageType[] | null;
  hasGarage?: boolean | null;
  garageLocations?: AcceptanceRuleGarageLocation[] | null;
  // Plan
  planIds?: string[] | null;
  variantIds?: string[] | null;
  // Geography
  ufCodes?: string[] | null;
  cityIds?: string[] | null;
  zoneIds?: string[] | null;
  // Driver
  driverAgeMin?: number | null;
  driverAgeMax?: number | null;
  cnhTenureMonthsMin?: number | null;
  isPj?: boolean | null;
  // Source
  channels?: AcceptanceRuleChannel[] | null;
  // Validity
  validFrom: string;
  validTo?: string | null;
  isActive?: boolean;
}

export function useUpsertAcceptanceRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertAcceptanceRuleInput) => {
      if (input.id) {
        const { id, ...body } = input;
        const res = await apiClient.put<{ data: AdminAcceptanceRule }>(`${BASE}/${id}`, body);
        return res.data.data;
      }
      const res = await apiClient.post<{ data: AdminAcceptanceRule }>(BASE, input);
      return res.data.data;
    },
    onSuccess: () => {
      // Phase 08.4 Plan 05 — invalida CS keys junto com admin keys: mutations
      // em entidade publicada disparam CS automático (SD-1 Phase 08), banner
      // global + chips csStatus precisam refletir mudança imediatamente.
      qc.invalidateQueries({ queryKey: pricingAdminKeys.all });
      qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    },
  });
}

export function useDeleteAcceptanceRuleAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      await apiClient.delete(`${BASE}/${id}`, { data: { reason } });
      return { id };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingAdminKeys.all });
      qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    },
  });
}

/**
 * Submit do entry (draft|rejected → approval_pending). Aceita ID via mutate(),
 * usado pelo "Salvar e enviar para aprovação" do AcceptanceRuleEditModal em
 * mode='create' (workflow.submit do useApprovalWorkflow precisa do id no path,
 * que ainda não existe no momento da instância).
 */
export function useSubmitAcceptanceRuleEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.post(`${BASE}/${id}/submit`);
      return { id };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingAdminKeys.all });
      qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    },
  });
}

export interface EvaluateAcceptanceInput {
  vehicle?: Record<string, unknown>;
  driver?: Record<string, unknown>;
  cep?: string;
  [extra: string]: unknown;
}

export function useEvaluateAcceptance() {
  return useMutation({
    mutationFn: async (input: EvaluateAcceptanceInput) => {
      const res = await apiClient.post<{ data: AcceptanceEvaluation }>(`${BASE}/evaluate`, input);
      return res.data.data;
    },
  });
}
